/**
 * Spacing utilities shared across layout atoms.
 *
 * Goal:
 * - Reduce "magic numbers" in specs by supporting semantic tokens: xs/sm/md/lg/xl/2xl
 * - Keep backward compatibility with numeric spacing scales used today
 *
 * Web note:
 * - Inline/Grid use CSS `gap` style with `calc(var(--spacing) * N)` (as you already do).
 * - Stack uses Tailwind `gap-*` classes, so it needs a numeric scale value.
 */

export type SpaceToken = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export const SPACE_TOKEN_TO_UNITS: Record<SpaceToken, number> = {
  xs: 1,
  sm: 2,
  md: 4,
  lg: 6,
  xl: 8,
  '2xl': 10,
};

function isSpaceToken(value: unknown): value is SpaceToken {
  if (typeof value !== 'string') return false;
  const v = value.trim().toLowerCase();
  return v === 'xs' || v === 'sm' || v === 'md' || v === 'lg' || v === 'xl' || v === '2xl';
}

function isNumericString(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value.trim());
}

/**
 * Convert a spacing value into "units" for Tailwind-ish scale usage (gap classes, padding classes).
 * - token => mapped units
 * - number => clamped to finite integer-ish value (tools may still pass floats; we round)
 * - numeric string => parsed
 * - otherwise => fallback
 */
export function resolveSpaceUnits(value: unknown, fallbackUnits: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);

  if (typeof value === 'string') {
    const raw = value.trim().toLowerCase();
    if (!raw) return fallbackUnits;

    if (isSpaceToken(raw)) return SPACE_TOKEN_TO_UNITS[raw];

    if (isNumericString(raw)) return Math.round(Number.parseFloat(raw));
  }

  return fallbackUnits;
}

/**
 * Convert a spacing value into a CSS length for style props (Inline/Grid/Spacer).
 * - token => calc(var(--spacing) * units)
 * - number => calc(var(--spacing) * number)
 * - numeric string => calc(var(--spacing) * number)
 * - CSS length => used as-is (best-effort)
 */
export function resolveSpaceCssLength(value: unknown, fallbackUnits: number): string | undefined {
  if (value === undefined || value === null) return undefined;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return `calc(var(--spacing) * ${value})`;
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return undefined;

    const lower = raw.toLowerCase();
    if (isSpaceToken(lower)) return `calc(var(--spacing) * ${SPACE_TOKEN_TO_UNITS[lower]})`;
    if (isNumericString(raw)) return `calc(var(--spacing) * ${Number.parseFloat(raw)})`;

    // Best-effort CSS length passthrough (e.g. "12px", "1rem", "clamp(...)")
    return raw;
  }

  return `calc(var(--spacing) * ${fallbackUnits})`;
}
